import { useEffect, useRef } from 'react'
import { readAppRouteMode } from '../appRouteMode'
import { isDemoSession } from '../data/demoSession'
import { getAnalytics, track } from './instance'

const MAX_TEXT = 4000
const MAX_STACK = 6000
const MAX_ARRAY_ITEMS = 25
const MAX_OBJECT_KEYS = 80

const SENSITIVE_KEY_RE = /(?:authorization|cookie|password|passwd|secret|token|access[_-]?token|refresh[_-]?token|api[_-]?key|credential|session[_-]?token|id[_-]?token)/i
const TOKEN_VALUE_RE = /\b(?:Bearer\s+)?[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{10,}\b/g
const LONG_SECRET_RE = /\b[A-Za-z0-9_~+/=-]{48,}\b/g

type ConsoleEntry = {
  ts: string
  level: 'warn' | 'error'
  text: string
}

const recentConsole: ConsoleEntry[] = []
let consoleInstalled = false
let globalTelemetryInstalled = false
const visibleErrorDedup = new Map<string, number>()

const trimText = (value: unknown, max = MAX_TEXT): string => {
  const s = String(value ?? '')
  return s.length > max ? `${s.slice(0, max)}...<truncated>` : s
}

export function redactTelemetryText(raw: unknown, max = MAX_TEXT): string {
  let text = trimText(raw, max)
  text = text.replace(/([?&](?:token|access_token|refresh_token|code|secret|password|api_key)=)[^&#]+/gi, '$1<redacted>')
  text = text.replace(/(authorization\s*[:=]\s*bearer\s+)[^\s,"'}]+/gi, '$1<redacted>')
  text = text.replace(/(cookie\s*[:=]\s*)[^,"'}]+/gi, '$1<redacted>')
  text = text.replace(TOKEN_VALUE_RE, '<redacted-token>')
  text = text.replace(LONG_SECRET_RE, '<redacted-secret>')
  return text
}

export function redactTelemetryValue(value: unknown, depth = 0): unknown {
  if (value == null) return value
  if (typeof value === 'string') return redactTelemetryText(value)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (value instanceof Error) {
    return {
      name: redactTelemetryText(value.name, 240),
      message: redactTelemetryText(value.message),
      stack: redactTelemetryText(value.stack || '', MAX_STACK),
    }
  }
  if (depth >= 5) return '<truncated>'
  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map((item) => redactTelemetryValue(item, depth + 1))
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    let count = 0
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (count >= MAX_OBJECT_KEYS) {
        out.__truncated = true
        break
      }
      count += 1
      if (SENSITIVE_KEY_RE.test(key)) {
        out[key] = '<redacted>'
      } else {
        out[key] = redactTelemetryValue(child, depth + 1)
      }
    }
    return out
  }
  return redactTelemetryText(value)
}

const serializeConsoleArgs = (args: unknown[]): string => {
  try {
    return args
      .map((arg) => {
        if (typeof arg === 'string') return arg
        if (arg instanceof Error) return arg.stack || arg.message
        return JSON.stringify(redactTelemetryValue(arg))
      })
      .join(' ')
  } catch {
    return args.map((arg) => String(arg)).join(' ')
  }
}

function installConsoleCapture(): void {
  if (consoleInstalled || typeof window === 'undefined') return
  consoleInstalled = true
  for (const level of ['warn', 'error'] as const) {
    const original = console[level]
    console[level] = (...args: unknown[]) => {
      try {
        recentConsole.push({
          ts: new Date().toISOString(),
          level,
          text: redactTelemetryText(serializeConsoleArgs(args), 1200),
        })
        while (recentConsole.length > 20) recentConsole.shift()
      } catch {
        // Console capture must never break console behavior.
      }
      return original.apply(console, args as any)
    }
  }
}

function routeContext() {
  if (typeof window === 'undefined') return { path: '', app_route_mode: 'unknown' }
  const loc = window.location
  const parts = loc.pathname.split('/').filter(Boolean)
  const workbookIndex = parts.indexOf('workbooks')
  const workbookId = workbookIndex >= 0 ? parts[workbookIndex + 1] || null : null
  const siteSlug = workbookIndex > 0 ? parts[0] || null : parts.length >= 2 && parts[0] !== 'admin' ? parts[0] : null
  const cfoIndex = parts.indexOf('cfo')
  const cfoPage = cfoIndex >= 0 ? parts[cfoIndex + 1] || 'home' : null
  return {
    path: loc.pathname,
    search: redactTelemetryText(loc.search || '', 1000),
    hash: redactTelemetryText(loc.hash || '', 500),
    app_route_mode: readAppRouteMode(loc),
    // Lets the founder activity dashboard split public-demo traffic from real
    // customers without joining back to users.signup_source.
    is_demo: isDemoSession(),
    site_slug: siteSlug,
    workbook_id: workbookId,
    cfo_page: cfoPage,
    page_title: typeof document !== 'undefined' ? redactTelemetryText(document.title || '', 500) : '',
    user_agent: typeof navigator !== 'undefined' ? redactTelemetryText(navigator.userAgent || '', 1000) : '',
  }
}

function trackActivity(event: string, props: Record<string, unknown>, surface?: string | null, workbookId?: string | null): void {
  track({
    event,
    surface: surface || String(props.app_route_mode || '') || null,
    workbook_id: workbookId || (typeof props.workbook_id === 'string' ? props.workbook_id : null),
    props: redactTelemetryValue({
      ...props,
      recent_console: recentConsole.slice(-8),
    }) as Record<string, unknown>,
  })
}

function errorPayload(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack || '',
    }
  }
  return { message: typeof error === 'string' ? error : serializeConsoleArgs([error]) }
}

function installVisibleErrorObserver(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return
  const selector = [
    '[role="alert"]',
    '.cfo-shell-notice.error',
    '.cfo-statement-export-status.error',
    '.cfo-ar-export-status.is-error',
    '.cfo-analysis-modal-copytoast.failed',
  ].join(',')
  const inspect = (node: ParentNode) => {
    const nodes: Element[] = []
    if ((node as Element).matches?.(selector)) nodes.push(node as Element)
    nodes.push(...Array.from(node.querySelectorAll?.(selector) || []))
    for (const el of nodes) {
      const message = redactTelemetryText((el.textContent || '').replace(/\s+/g, ' ').trim(), 1200)
      if (!message) continue
      const route = routeContext()
      const key = `${route.path}|${message}`
      const now = Date.now()
      if ((visibleErrorDedup.get(key) || 0) > now - 15_000) continue
      visibleErrorDedup.set(key, now)
      trackActivity('app.visible_error', {
        ...route,
        message,
        element_class: redactTelemetryText((el as HTMLElement).className || '', 500),
        element_role: el.getAttribute('role') || null,
      })
    }
    for (const [key, ts] of Array.from(visibleErrorDedup.entries())) {
      if (ts < Date.now() - 60_000) visibleErrorDedup.delete(key)
    }
  }
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) inspect(node as ParentNode)
      })
    }
  })
  observer.observe(document.documentElement, { childList: true, subtree: true })
  inspect(document)
}

export function installGlobalActivityTelemetry(): void {
  if (globalTelemetryInstalled || typeof window === 'undefined') return
  globalTelemetryInstalled = true
  installConsoleCapture()

  window.addEventListener('error', (event) => {
    const route = routeContext()
    trackActivity('app.runtime_error', {
      ...route,
      ...errorPayload(event.error || event.message),
      filename: event.filename || null,
      lineno: event.lineno || null,
      colno: event.colno || null,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const route = routeContext()
    trackActivity('app.unhandled_rejection', {
      ...route,
      ...errorPayload(event.reason),
    })
  })

  window.addEventListener('fusegrid_api_error', ((event: CustomEvent) => {
    const route = routeContext()
    const detail = (event.detail || {}) as Record<string, unknown>
    trackActivity('app.api_error', {
      ...route,
      status: detail.status ?? null,
      message: detail.message ?? null,
      url: detail.url ?? null,
      request_path: detail.path ?? null,
      kind: detail.kind ?? 'api',
      body_preview: detail.message ?? null,
    })
  }) as EventListener)

  installVisibleErrorObserver()
}

export function useRouteActivityTelemetry(): void {
  const currentRef = useRef<{ path: string; startedAt: number; route: ReturnType<typeof routeContext> } | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const finishCurrent = () => {
      const current = currentRef.current
      if (!current) return
      const durationMs = Math.max(0, Math.round(Date.now() - current.startedAt))
      if (durationMs < 250) return
      const route = current.route
      trackActivity('app.page_dwell', {
        ...route,
        previous_path: current.path,
        duration_ms: durationMs,
        analytics_session_id: getAnalytics().session,
      })
    }
    const startCurrent = () => {
      finishCurrent()
      const route = routeContext()
      currentRef.current = { path: route.path, route, startedAt: Date.now() }
      trackActivity('app.page_view', {
        ...route,
        analytics_session_id: getAnalytics().session,
        referrer: redactTelemetryText(document.referrer || '', 1000),
      })
    }
    startCurrent()
    window.addEventListener('popstate', startCurrent)
    window.addEventListener('fg_route_change', startCurrent as EventListener)
    window.addEventListener('pagehide', finishCurrent)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') finishCurrent()
      else {
        const route = routeContext()
        currentRef.current = { path: route.path, route, startedAt: Date.now() }
      }
    })
    return () => {
      finishCurrent()
      window.removeEventListener('popstate', startCurrent)
      window.removeEventListener('fg_route_change', startCurrent as EventListener)
      window.removeEventListener('pagehide', finishCurrent)
    }
  }, [])
}
